export type LoggedOutPlanTier = "starter" | "premium" | "ultimate"
export type LoggedOutBillingPeriod = "monthly" | "yearly"
export type LoggedOutCheckoutSource = "subscription_page" | "pricing_page" | "paywall_modal"

const PLAN_TIERS = new Set<LoggedOutPlanTier>(["starter", "premium", "ultimate"])
const BILLING_PERIODS = new Set<LoggedOutBillingPeriod>(["monthly", "yearly"])
const SOURCES = new Set<LoggedOutCheckoutSource>([
  "subscription_page",
  "pricing_page",
  "paywall_modal",
])
const INTENT_RE = /^[a-z_]{1,40}$/
const CHARACTER_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const EXPERIMENT_RE = /^[a-z0-9-]{1,80}$/

export type LoggedOutPlanRedirectInput = {
  tier: LoggedOutPlanTier
  billing: LoggedOutBillingPeriod
  source: LoggedOutCheckoutSource
  intent?: string | null
  characterId?: string | null
  paywallExperimentFlag?: string | null
  paywallExperimentVariant?: string | null
  presentation?: "page" | "modal"
  pathname?: string | null
}

function safePath(value: string | null | undefined): string | null {
  if (!value || !value.startsWith("/") || value.startsWith("//") || value.includes("://")) {
    return null
  }
  return value
}

/** Build the logged-out register URL for the plan that was just clicked. */
export function loggedOutRegisterHref(input: LoggedOutPlanRedirectInput): string {
  if (!PLAN_TIERS.has(input.tier) || !BILLING_PERIODS.has(input.billing) || !SOURCES.has(input.source)) {
    return "/register?redirect=%2Fsubscription"
  }

  const params = new URLSearchParams()
  if (input.presentation === "modal") params.set("paywall", "subscribe")
  if (input.intent && INTENT_RE.test(input.intent)) {
    params.set(input.presentation === "modal" ? "paywallIntent" : "intent", input.intent)
  }
  const characterId =
    input.characterId && CHARACTER_ID_RE.test(input.characterId) ? input.characterId : null
  if (characterId) {
    params.set(input.presentation === "modal" ? "paywallCharacterId" : "characterId", characterId)
  }
  if (
    input.paywallExperimentFlag &&
    input.paywallExperimentVariant &&
    EXPERIMENT_RE.test(input.paywallExperimentFlag) &&
    EXPERIMENT_RE.test(input.paywallExperimentVariant)
  ) {
    params.set("paywallExperimentFlag", input.paywallExperimentFlag)
    params.set("paywallExperimentVariant", input.paywallExperimentVariant)
  }
  params.set("tier", input.tier)
  params.set("billing", input.billing)
  if (input.source !== "subscription_page") params.set("source", input.source)

  const base =
    input.presentation === "modal"
      ? (safePath(input.pathname) ?? "/subscription")
      : "/subscription"
  const returnPath = `${base}?${params.toString()}`
  const register = new URLSearchParams({ redirect: returnPath })
  if (characterId) register.set("characterId", characterId)
  return `/register?${register.toString()}`
}
